import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import UtilsHelper from "./utilsHelper";
import { InvoiceFormTypes } from "models/formTypes";
import { InvoiceConstants } from "utils/constants";
import LocalizationHelperInstance from "./localizationHelper";
import { TranslationKeys } from "translations";
import { CountryEnums } from "models/enums";

const InvoiceHelper = {
  formatInvoiceNumber: (invoiceNumber: number) => {
    return ("000000" + invoiceNumber).slice(-7);
  },

  fillSerialNumberTemplate: (text: string, serialNumber?: string) => {
    if (!text || !serialNumber) return text;

    const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Invoice.InvoiceNumberHashTag
    );

    if (text.includes(InvoiceConstants.SerialNumberTemplateValue)) {
      return text.replace(InvoiceConstants.SerialNumberTemplateValue, serialNumber);
    }
    if (text.includes(serialNumberTemplateValueDisplayText)) {
      return text.replace(serialNumberTemplateValueDisplayText, serialNumber);
    }

    return text;
  },

  revertSerialNumberToTemplate: (text?: string, serialNumber?: string) => {
    if (!text) return text;

    const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Invoice.InvoiceNumberHashTag
    );

    if (text.includes(serialNumberTemplateValueDisplayText)) {
      return text.replace(serialNumberTemplateValueDisplayText, InvoiceConstants.SerialNumberTemplateValue);
    }

    return serialNumber ? text?.replace(serialNumber, InvoiceConstants.SerialNumberTemplateValue) : text;
  },

  getEmailToType: (
    defaultEmailTo?: InvoiceFormTypes.DefaultEmailTo,
    emailTo?: AutocompleteListItem[]
  ): WintSuperkollAPIModel.SendInvoiceMailsToEnum => {
    const defaultEmailsCompany = defaultEmailTo?.customer
      ? UtilsHelper.splitMultipleEmails(defaultEmailTo?.customer)
      : [];
    const defaultEmailsReferecePerson = defaultEmailTo?.referencePerson
      ? UtilsHelper.splitMultipleEmails(defaultEmailTo?.referencePerson)
      : [];

    const emailToList = emailTo?.map((i) => i.value) ?? [];

    if (emailToList.length === 0) {
      return WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other;
    }

    if (UtilsHelper.areStringArraysEqual(defaultEmailsCompany, emailToList)) {
      return WintSuperkollAPIModel.SendInvoiceMailsToEnum.CustomerCompany;
    }

    if (UtilsHelper.areStringArraysEqual(defaultEmailsReferecePerson, emailToList)) {
      return WintSuperkollAPIModel.SendInvoiceMailsToEnum.ReferencePerson;
    }

    return WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other;
  },

  getTodoSubType: (status: WintSuperkollAPIModel.InvoiceStatus) => {
    return status === WintSuperkollAPIModel.InvoiceStatus.NotSent
      ? WintSuperkollAPIModel.TodoSubType.InvoiceDraft
      : WintSuperkollAPIModel.TodoSubType.InvoiceOverdue;
  },

  // Single source of truth for "is this row's VAT locked to its article's rate": the row's
  // selected article has a configured revenue account (articleAccount) AND a known article VAT
  // (articleVat) to lock to, and the customer is domestic. Both articleAccount and articleVat are
  // required - without a known articleVat there is no rate to lock the row to, so a row can't be
  // considered locked. Used both to decide whether to render the VAT select as read-only
  // (productRow.tsx) and, layered with an extra "does the current tax already match?" check, to
  // decide whether a row's drifted tax should be snapped back on customer change
  // (customerSelectionStep.tsx) - being locked and needing a reset right now are different
  // questions, so that extra check stays at that call site rather than in here.
  isRowVatLocked: (
    product: Pick<InvoiceFormTypes.Product, "articleAccount" | "articleVat" | "articleIsAgentSale">,
    isDomesticCustomer: boolean | undefined,
    revenueAccountsEnabled: boolean | undefined,
    noVatCompany: boolean | undefined
  ): boolean => {
    // Strictly opt-in per company (decision R11). The article's account is returned regardless of
    // the flag, and production already has thousands of articles carrying one (mostly the agent
    // flow's system articles, but hundreds of ordinary articles too) - so without this gate a
    // company that never enabled the feature would get read-only VAT fields explained by a tooltip
    // about a feature it does not have.
    if (!revenueAccountsEnabled) {
      return false;
    }

    // Non-VAT-registered companies (momsbefriade) are exempt server-side: their rows are force-
    // zeroed while the article keeps its own rate, so the rule could never be satisfied. Locking
    // here would freeze a rate the company must never charge, with no way to correct it back to 0.
    if (noVatCompany) {
      return false;
    }

    // Agent-sale system articles are exempt server-side too (R2-1): ValidateItemRevenueAccountVat
    // skips them, so locking the row here would enforce a rule the server does not.
    if (product.articleIsAgentSale) {
      return false;
    }

    return product.articleAccount !== undefined && product.articleVat !== undefined && !!isDomesticCustomer;
  },

  // Mirrors GlobalInvoiceSystemStatic.IsToForeignCountry, inverted: the BILLING address country
  // only, compared case-insensitively, with a missing/empty country reading as Sweden. That server
  // rule is what actually rejects a send, so every client-side "is this customer domestic" decision
  // goes through here rather than through IsEuCustomer's address precedence, which is a different
  // rule for a different purpose. Using the delivery address would let a customer with a Swedish
  // billing address and a foreign delivery address edit VAT freely and then be refused at send.
  isDomesticCountryCode: (countryCode: string | undefined): boolean =>
    !countryCode || countryCode.toUpperCase() === CountryEnums.CountryCode.SE.toUpperCase(),

  // Which rows must snap back to their article's VAT: locked rows whose current tax has drifted
  // from the article's. "Locked" and "needs a reset right now" are different questions, so the
  // drift check lives here rather than in isRowVatLocked. Shared by the customer-change path
  // (customerSelectionStep) and the open-existing-invoice path (customerInformationLoader) so the
  // two can't disagree. Callers decide this BEFORE setValues - a state updater must stay pure.
  //
  // Credit invoices never reset: a credit row's VAT is a carried-forward historical value, not
  // something chosen against today's article config - mirroring Core's ValidateItemRevenueAccountVat,
  // which returns immediately for credits without evaluating this rule at all. Rewriting it would
  // fight "kreditera och redigera", whose entire point is to preserve the credited invoice's
  // original figures. The exemption is a parameter here rather than a guard in each caller - like
  // noVatCompany - because a caller-side guard is one a new caller can omit, and the rows it would
  // then rewrite are exactly the ones the exemption exists to protect.
  getRowsNeedingVatReset: (
    products: InvoiceFormTypes.Product[] | undefined,
    isDomesticCustomer: boolean | undefined,
    revenueAccountsEnabled: boolean | undefined,
    noVatCompany: boolean | undefined,
    creditedInvoiceId: number | undefined
  ): Set<InvoiceFormTypes.Product["uniqueId"]> =>
    creditedInvoiceId !== undefined
      ? new Set()
      : new Set(
          (products ?? [])
            .filter(
              (p) =>
                InvoiceHelper.isRowVatLocked(p, isDomesticCustomer, revenueAccountsEnabled, noVatCompany) &&
                p.tax !== p.articleVat
            )
            .map((p) => p.uniqueId)
        ),
};

export default InvoiceHelper;
